import React, { useCallback, useMemo } from "react";
import { graphql, useFragment, usePaginationFragment } from "react-relay";

import { faBars, faChevronDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { format, getMinutes } from "date-fns";
import { compact } from "lodash";

import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getDateString } from "~/lib/utils";
import { IconButton } from "~/ui/button/IconButton";
import Menu from "~/ui/menu";

import type { SeriesExplorer_note$key } from "~/__generated__/SeriesExplorer_note.graphql";
import type { SeriesExplorer_notes$key } from "~/__generated__/SeriesExplorer_notes.graphql";
import {
	default as seriesExplorerNotesRefetchQuery,
	type SeriesExplorerNotesRefetchQuery,
} from "~/__generated__/SeriesExplorerNotesRefetchQuery.graphql";

import styles from "./SeriesExplorer.module.css";

function formatMeetingDate(startTime: Date, isAllDay: boolean) {
	// eslint-disable-next-line max-len
	return `${getDateString(startTime)}${!isAllDay ? ` at ${format(startTime, `h${getMinutes(startTime) > 0 ? ":mm" : ""} a`)}` : ""}`;
}

graphql`
	fragment SeriesExplorer_linkable_note on Note @relay(mask: false) {
		id
		createdAt
		eventStart
		url
		event {
			isAllDay
		}
	}
`;

const noteFragment = graphql`
	fragment SeriesExplorer_note on Note {
		id
		stream {
			id
			nextEvent(onlyEmpty: false) {
				guid
				start
				end
				isAllDay
				note {
					...SeriesExplorer_linkable_note @relay(mask: false)
				}
			}
		}
		previousNote {
			...SeriesExplorer_linkable_note @relay(mask: false)
		}
	}
`;

const paginationFragment = graphql`
	fragment SeriesExplorer_notes on NoteStream @refetchable(queryName: "SeriesExplorerNotesRefetchQuery") {
		notes(
			first: $first
			after: $after
			last: $last
			before: $before
			currentEventGuid: $eventGuid
			startFromNote: $startFromNote
		) @connection(key: "SeriesExplorer_notes", filters: []) {
			pageInfo {
				endCursor
				hasPreviousPage
				startCursor
			}

			edges {
				cursor
				node {
					...SeriesExplorer_linkable_note @relay(mask: false)
				}
			}
		}
	}
`;

function LoadingMeetings() {
	return (
		<>
			<Menu.LoadingItem animationDelayIndex={0} />
			<Menu.LoadingItem animationDelayIndex={1} />
			<Menu.LoadingItem animationDelayIndex={2} />
			<Menu.LoadingItem animationDelayIndex={3} />
			<Menu.LoadingItem animationDelayIndex={4} />
		</>
	);
}

function PreviousMeetings({
	streamId,
	previousNoteId,
	currentNoteId,
}: {
	streamId: string;
	previousNoteId: string;
	currentNoteId: string;
}) {
	const data = useLazyLoadQuery<SeriesExplorerNotesRefetchQuery>(
		seriesExplorerNotesRefetchQuery,
		{ id: streamId, first: 10, startFromNote: previousNoteId },
		{ fetchPolicy: "store-and-network" },
	);
	const pagination = usePaginationFragment<SeriesExplorerNotesRefetchQuery, SeriesExplorer_notes$key>(
		paginationFragment,
		data.node,
	);

	const notes = useMemo(
		() =>
			compact(
				pagination.data?.notes?.edges.map(
					edge =>
						edge?.node &&
						edge.node.url &&
						edge.node.createdAt && {
							id: edge.node.id,
							start: edge.node.eventStart ? new Date(edge.node.eventStart) : new Date(edge.node.createdAt),
							isAllDay: edge.node.event?.isAllDay ?? false,
							url: edge.node.url,
						},
				),
			),
		[pagination.data],
	);

	const trackOtherMeetingClick = useCallback(() => {
		dispatchAnalyticsTrack("Opened other meeting in series", { location: "series_explorer", futureMeeting: false });
	}, []);

	const onLoadAfter = useCallback(() => {
		if (!pagination.hasNext) return;
		pagination.loadNext(10);
	}, [pagination]);

	return (
		<>
			{notes.map(note => (
				<Menu.ItemLink
					key={note.id}
					to={note.url}
					onClick={trackOtherMeetingClick}
					className={cx({ [styles.isOnscreen]: note.id === currentNoteId })}
				>
					{note.start ? formatMeetingDate(note.start, note.isAllDay) : "Unscheduled note"}
				</Menu.ItemLink>
			))}
			{pagination.hasNext &&
				(pagination.isLoadingNext ? (
					<LoadingMeetings />
				) : (
					<>
						<button className={styles.loadMoreButton} onClick={onLoadAfter}>
							Load more&ensp;
							<FontAwesomeIcon icon={faChevronDown} fontSize="0.8em" />
						</button>
					</>
				))}
		</>
	);
}

export default function SeriesExplorer({ note: noteKey }: { note: SeriesExplorer_note$key }) {
	const note = useFragment(noteFragment, noteKey);

	const previousMeeting = useMemo(() => {
		if (!note.previousNote || !note.previousNote.createdAt || !note.previousNote.url) return null;

		return {
			id: note.previousNote.id,
			start: note.previousNote.eventStart
				? new Date(note.previousNote.eventStart)
				: new Date(note.previousNote.createdAt),
			isAllDay: note.previousNote.event?.isAllDay ?? false,
			url: note.previousNote.url,
		};
	}, [note.previousNote]);

	const nextEventNote = useMemo(() => {
		const nextEvent = note.stream.nextEvent;
		if (!nextEvent || !nextEvent.start) return null;

		return {
			start: new Date(nextEvent.start),
			isAllDay: nextEvent.isAllDay,
			/**
			 * Sometimes the next event does not have a note created yet.
			 * In this case, the URL for this option uses the event guid.
			 * This avoids creating extra notes just by visiting this one.
			 */
			url: nextEvent.note?.url ?? `/meetings/${nextEvent.guid}/`,
			isOnscreen: note.id === nextEvent.note?.id,
		};
	}, [note.id, note.stream.nextEvent]);

	/**
	 * TODO: If there are *only* meetings that follow this one that are in the past (i.e no future meetings, but
	 * there are a few past meetings), this check will hide the series explorer.
	 *
	 * This menu should show those meetings, but right now we only show "previous" meetings (i.e. meetings that precede
	 * this one). This is an obvious gap in user experience.
	 */
	if (!previousMeeting && !nextEventNote) return null;

	return (
		<Menu>
			<Menu.Trigger asChild>
				<IconButton
					icon={faBars}
					variant="tertiary"
					iconProps={{
						className: cx(styles.menuTriggerIcon, {
							[styles.indicateNextMeeting]: nextEventNote?.isOnscreen === false,
						}),
					}}
					className={cx(styles.menuTrigger, { [styles.indicateNextMeeting]: nextEventNote?.isOnscreen === false })}
				/>
			</Menu.Trigger>
			<Menu.Content>
				{nextEventNote && (
					<>
						<Menu.Label>Next upcoming meeting</Menu.Label>
						<Menu.ItemLink
							to={nextEventNote.url}
							className={cx(styles.nextUpcomingMeetingItem, {
								[styles.isOnscreen]: nextEventNote.isOnscreen,
							})}
						>
							{!nextEventNote.isOnscreen && <div className={styles.upcomingMeetingIndicator} />}
							{formatMeetingDate(nextEventNote.start, nextEventNote.isAllDay)}
						</Menu.ItemLink>
					</>
				)}
				{previousMeeting && (
					<>
						{nextEventNote && <Menu.Separator />}
						<Menu.Label>Previous meetings</Menu.Label>
						<div className={styles.previousMeetings}>
							<React.Suspense
								fallback={
									<>
										<Menu.ItemLink to={previousMeeting.url}>
											{formatMeetingDate(previousMeeting.start, previousMeeting.isAllDay)}
										</Menu.ItemLink>
										<LoadingMeetings />
									</>
								}
							>
								<PreviousMeetings
									streamId={note.stream.id}
									previousNoteId={previousMeeting.id}
									currentNoteId={note.id}
								/>
							</React.Suspense>
						</div>
					</>
				)}
			</Menu.Content>
		</Menu>
	);
}
